import styled from "@emotion/styled";
import { Trans, useTranslation } from "react-i18next";
import { outputP3ColorFromRGB } from "../helpers/colors";
import pJson from "../../package.json";

const TermsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 1em auto;
  padding: 0 2em;
`;

const Warning = styled.strong`
  ${outputP3ColorFromRGB([255, 255, 0])}
`;

/** Third-party services data is sent to. Names/URLs are stable; only the
 * purpose sentence is translated. */
const THIRD_PARTIES: { name: string; url: string; purpose: string }[] = [
  {
    name: "OpenStreetMap's Nominatim service",
    url: "https://nominatim.openstreetmap.org",
    purpose:
      "To retrieve a human readable location for given coordinates, and to find coordinates for a given query.",
  },
  {
    name: "Open-Meteo",
    url: "https://open-meteo.com",
    purpose:
      "To retrieve hourly weather, elevation, and time-zone information for your location.",
  },
  {
    name: "NOAA Aviation Weather Center Text Data Server",
    url: "https://aviationweather.gov/dataserver",
    purpose:
      "To retrieve nearby airport TAF reports, G‑AIRMETs, SIGMETs, and CWAs to enhance available weather information.",
  },
  {
    name: "National Weather Service API",
    url: "https://www.weather.gov/documentation/services-web-api",
    purpose: "To retrieve hourly weather information for your location.",
  },
  {
    name: "OpenStreetMap Tile Server",
    url: "https://operations.osmfoundation.org/policies/tiles/",
    purpose: "To provide maps for weather alerts.",
  },
  {
    name: "OGC Web Services",
    url: "https://mesonet.agron.iastate.edu/ogc",
    purpose: "To provide radar overlays on maps for weather alerts.",
  },
];

export default function Terms() {
  const { t } = useTranslation();
  const tr = t as unknown as (k: string) => string;

  return (
    <TermsContainer>
      <h2>{t("About")}</h2>

      <p>
        {t("Your device is currently using PPG.Treevolta version {{version}}.", {
          version: pJson.version,
        })}
      </p>

      <h2>{t("Attribution")}</h2>

      <p>
        <Trans
          i18nKey="Geocoding, reverse geocoding, and map tiles attribution"
          components={{
            osm: <a href="https://www.openstreetmap.org/copyright" />,
          }}
        />
      </p>

      <h2>{t("Privacy Policy")}</h2>

      <p>
        <strong>
          {t("PPG.Treevolta does not collect or share personal information.")}
        </strong>
      </p>

      <p>
        {t(
          "PPG.Treevolta does not use cookies. Your preferences are stored on your device, and are not transmitted to the server.",
        )}
      </p>

      <h3>{t("Third-party APIs")}</h3>

      <section>
        <strong>{t("We do not store geolocations.")}</strong>{" "}
        {t(
          "However, geolocations may be shared with the following third parties in order to provide you information:",
        )}
        <ol>
          {THIRD_PARTIES.map((tp, i) => (
            <li key={tp.name} style={{ marginTop: i === 0 ? 0 : "1em" }}>
              <strong>{tp.name}</strong> —{" "}
              <a href={tp.url} target="_blank" rel="noopener noreferrer">
                {tp.url}
              </a>
              <br />
              <strong>{t("Purpose:")}</strong> {tr(tp.purpose)}
            </li>
          ))}
        </ol>
      </section>

      <h2>{t("Terms of Use")}</h2>

      <p>
        <Warning>⚠️ {t("Warning! Fly at your own risk.")}</Warning>
      </p>

      <p>
        {t(
          "THE SOFTWARE IS PROVIDED \"AS IS\", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.",
        )}
      </p>
    </TermsContainer>
  );
}
